import { useTheme } from "@mui/material";
import { TermBand } from "./presidentialTerms";
import { WorldEvent } from "./worldEvents";
import { EraBand } from "./eras";

// Dependency-free SVG bar chart: one bar per month, positioned on a real
// calendar axis (so gaps in coverage show as empty space), height ∝ value.
// Conveys the *breadth* of the index at a glance; exact values are on hover.
//
// Single-series mode (`data`): one bar per month, height ∝ pages.
// Stacked mode (`stackedData` + `stacked`): each month's bar stacks the given
// series (e.g. broadcast vs podcast documents) in distinct colors, with a
// legend. Height ∝ the month's total across series.
//
// Two optional annotation lanes stack above the bars:
//   - `bands`  — labeled spans (presidential terms).
//   - `events` — point/range world events, with labels packed into as many
//     rows as needed so they never overlap.
// And one lane sits directly UNDER the bars:
//   - `eras`   — the dataset's content eras as contiguous labeled width bars.
// Faint dashed guides tie each annotation down to the bars.

export interface ChartSeries {
  key: string; // key into a StackedPoint.values
  label: string; // legend label
  color: string; // fill
}

interface StackedPoint {
  month: string;
  values: Record<string, number>;
}

interface Props {
  data?: { month: string; pages: number }[];
  stackedData?: StackedPoint[];
  stacked?: ChartSeries[]; // when set (with stackedData), bars stack these series
  unitLabel?: string; // hover/aria noun for single-series mode (default "pages")
  bands?: TermBand[];
  events?: WorldEvent[];
  eras?: EraBand[];
  barsHeight?: number;
}

const W = 1000;
const PAD_L = 10;
const PAD_R = 10;
const PAD_B = 22;
const RIBBON_H = 16;
const EVENT_ROW_H = 16;
const ERA_H = 18; // era width-bar lane, directly under the bars
const CHAR_W = 5.6; // rough helvetica advance at fontSize 10

const monthIdx = (m: string): number => {
  const [y, mo] = m.split("-").map(Number);
  return y * 12 + (mo - 1);
};

// Greedy row packing: place each [left,right] label extent in the lowest row
// whose previous item ends before it starts (+gap). Returns a row per item.
function packRows(extents: Array<[number, number]>): number[] {
  const order = extents.map((_, i) => i).sort((a, b) => extents[a][0] - extents[b][0]);
  const rowRight: number[] = [];
  const rowOf = new Array<number>(extents.length).fill(0);
  for (const i of order) {
    const [l, r] = extents[i];
    let row = rowRight.findIndex((right) => l >= right + 4);
    if (row === -1) {
      row = rowRight.length;
      rowRight.push(r);
    } else {
      rowRight[row] = r;
    }
    rowOf[i] = row;
  }
  return rowOf;
}

type RenderedEvent =
  | { kind: "point"; label: string; x: number; labelX: number; ext: [number, number] }
  | { kind: "range"; label: string; x0: number; x1: number; labelX: number; ext: [number, number] };

export function TimelineBarChart({
  data,
  stackedData,
  stacked,
  unitLabel = "pages",
  bands,
  events,
  eras,
  barsHeight = 120,
}: Props) {
  const theme = useTheme();

  const isStacked = !!(stacked && stacked.length && stackedData && stackedData.length);
  // Unified month axis: both modes expose {month} points in calendar order.
  const months: string[] = isStacked
    ? stackedData!.map((p) => p.month)
    : (data ?? []).map((d) => d.month);
  if (months.length === 0) return null;

  const sumOf = (p: StackedPoint): number =>
    (stacked ?? []).reduce((acc, s) => acc + (p.values[s.key] ?? 0), 0);

  const min = monthIdx(months[0]);
  const max = monthIdx(months[months.length - 1]);
  const span = Math.max(1, max - min + 1);
  const innerW = W - PAD_L - PAD_R;
  const barW = innerW / span;
  const maxVal = isStacked
    ? Math.max(1, ...stackedData!.map(sumOf))
    : Math.max(1, ...(data ?? []).map((d) => d.pages));

  const xOf = (m: string): number => PAD_L + (monthIdx(m) - min) * barW;
  const clampX = (x: number): number => Math.max(PAD_L, Math.min(W - PAD_R, x));

  const presH = bands && bands.length ? RIBBON_H : 0;

  // Build renderable events (skip out-of-range points; clip ranges).
  const evs: RenderedEvent[] = [];
  for (const e of events ?? []) {
    const labelW = e.label.length * CHAR_W;
    if (e.end) {
      const x0 = clampX(xOf(e.date));
      const x1 = clampX(xOf(e.end));
      if (x1 - x0 < 1) continue;
      const center = (x0 + x1) / 2;
      evs.push({
        kind: "range",
        label: e.label,
        x0,
        x1,
        labelX: center,
        ext: [Math.min(x0, center - labelW / 2), Math.max(x1, center + labelW / 2)],
      });
    } else {
      const x = xOf(e.date);
      if (x < PAD_L - 0.5 || x > W - PAD_R + 0.5) continue;
      evs.push({ kind: "point", label: e.label, x, labelX: x + 5, ext: [x, x + 5 + labelW] });
    }
  }
  const evRows = packRows(evs.map((e) => e.ext));
  const nEvRows = evRows.length ? Math.max(...evRows) + 1 : 0;
  const evTop = 4 + presH + (presH ? 4 : 0);
  const eventsH = nEvRows ? nEvRows * EVENT_ROW_H + 4 : 0;

  const padT = 4 + presH + eventsH + 4;
  const innerH = barsHeight;
  const baseY = padT + innerH;
  // Era lane sits between the bars baseline and the year axis.
  const eraLaneH = eras && eras.length ? ERA_H + 4 : 0;
  const eraTop = baseY + 4;
  const axisY = baseY + eraLaneH; // where the year ticks/labels begin
  const H = axisY + PAD_B;

  const firstYear = Math.floor(min / 12);
  const lastYear = Math.floor(max / 12);
  const years: number[] = [];
  for (let y = firstYear; y <= lastYear; y++) years.push(y);
  const yearStep = Math.ceil(years.length / 14);

  const barColor = theme.palette.primary.main;
  const axis = theme.palette.divider;
  const muted = theme.palette.text.secondary;
  const ink = theme.palette.text.primary;
  const bandFills = [theme.palette.primary.main, theme.palette.secondary.main];
  // Era fills: grey = pre-Pagefeed (no Pages), blue = Pagefeed, green = Ad-Aware.
  const eraFills = [theme.palette.grey[500], theme.palette.info.main, theme.palette.success.main];

  const guide = (x: number, topY: number, key: string) => (
    <line
      key={key}
      x1={x}
      y1={topY}
      x2={x}
      y2={baseY}
      stroke={muted}
      strokeWidth={1}
      strokeDasharray="2 3"
      opacity={0.3}
    />
  );

  const svg = (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      preserveAspectRatio="none"
      role="img"
      aria-label={
        isStacked
          ? "Documents per month by corpus, with presidential terms, world events, and dataset eras"
          : `${unitLabel} per month with presidential terms, world events, and dataset eras`
      }
    >
      {/* presidential ribbon */}
      {(bands ?? []).map((b, i) => {
        const x0 = clampX(xOf(b.start));
        const x1 = clampX(xOf(b.end));
        const w = x1 - x0;
        if (w < 1) return null;
        return (
          <g key={`${b.label}-${b.start}`}>
            <rect
              x={x0}
              y={4}
              width={w}
              height={presH - 5}
              fill={bandFills[i % bandFills.length]}
              fillOpacity={0.18}
              stroke={axis}
              strokeWidth={0.5}
            />
            {w > 38 && (
              <text x={x0 + w / 2} y={presH - 2} fontSize={10} fill={muted} textAnchor="middle">
                {b.label}
              </text>
            )}
            <line
              x1={x0}
              y1={padT}
              x2={x0}
              y2={baseY}
              stroke={axis}
              strokeWidth={1}
              strokeDasharray="2 3"
              opacity={0.55}
            />
          </g>
        );
      })}

      {/* world events lane */}
      {evs.map((e, i) => {
        // Marker sits at the TOP of the row; the label sits BELOW it so the
        // range bar never cuts through its (centered) label text.
        const y = evTop + evRows[i] * EVENT_ROW_H;
        const my = y + 3;
        return (
          <g key={`${e.label}-${e.kind}`}>
            {e.kind === "point" ? (
              <>
                {guide(e.x, my, `g-${i}`)}
                <circle cx={e.x} cy={my} r={2.5} fill={muted} />
              </>
            ) : (
              <>
                {guide(e.x0, my, `g0-${i}`)}
                {guide(e.x1, my, `g1-${i}`)}
                <line x1={e.x0} y1={my} x2={e.x1} y2={my} stroke={muted} strokeWidth={2} />
                <line x1={e.x0} y1={my - 3} x2={e.x0} y2={my + 3} stroke={muted} strokeWidth={1} />
                <line x1={e.x1} y1={my - 3} x2={e.x1} y2={my + 3} stroke={muted} strokeWidth={1} />
              </>
            )}
            <text
              x={e.labelX}
              y={y + 13}
              fontSize={10}
              fill={ink}
              textAnchor={e.kind === "range" ? "middle" : "start"}
            >
              {e.label}
            </text>
          </g>
        );
      })}

      {/* baseline + bars */}
      <line x1={PAD_L} y1={baseY} x2={W - PAD_R} y2={baseY} stroke={axis} strokeWidth={1} />
      {isStacked
        ? stackedData!.map((p) => {
            const x = PAD_L + (monthIdx(p.month) - min) * barW;
            let acc = 0;
            return (
              <g key={p.month}>
                {(stacked ?? []).map((s) => {
                  const v = p.values[s.key] ?? 0;
                  if (v <= 0) return null;
                  const h = (v / maxVal) * innerH;
                  const y = baseY - acc - h;
                  acc += h;
                  return (
                    <rect
                      key={s.key}
                      x={x}
                      y={y}
                      width={Math.max(1, barW * 0.8)}
                      height={h}
                      fill={s.color}
                    >
                      <title>{`${p.month} — ${s.label}: ${v.toLocaleString()}`}</title>
                    </rect>
                  );
                })}
              </g>
            );
          })
        : (data ?? []).map((d) => {
            const x = PAD_L + (monthIdx(d.month) - min) * barW;
            const h = (d.pages / maxVal) * innerH;
            return (
              <rect
                key={d.month}
                x={x}
                y={baseY - h}
                width={Math.max(1, barW * 0.8)}
                height={h}
                fill={barColor}
              >
                <title>{`${d.month}: ${d.pages.toLocaleString()} ${unitLabel}`}</title>
              </rect>
            );
          })}

      {/* dataset era width-bars, directly under the bars */}
      {(eras ?? []).map((era, i) => {
        const x0 = clampX(xOf(era.start));
        const next = (eras ?? [])[i + 1];
        const x1 = next ? clampX(xOf(next.start)) : W - PAD_R;
        const w = x1 - x0;
        if (w < 1) return null;
        return (
          <g key={era.label}>
            <rect
              x={x0}
              y={eraTop}
              width={w}
              height={ERA_H}
              fill={eraFills[i % eraFills.length]}
              fillOpacity={0.3}
              stroke={axis}
              strokeWidth={0.5}
            >
              <title>{era.note ? `${era.label} — ${era.note}` : era.label}</title>
            </rect>
            {w > era.label.length * CHAR_W + 8 && (
              <text
                x={x0 + w / 2}
                y={eraTop + ERA_H / 2 + 3.5}
                fontSize={10}
                fill={ink}
                textAnchor="middle"
              >
                {era.label}
              </text>
            )}
          </g>
        );
      })}

      {/* year labels (below the era lane) */}
      {years.map((y, i) => {
        if (i % yearStep !== 0) return null;
        const x = PAD_L + (y * 12 - min) * barW;
        if (x < PAD_L - 1 || x > W - PAD_R + 1) return null;
        return (
          <g key={y}>
            <line x1={x} y1={axisY} x2={x} y2={axisY + 4} stroke={axis} strokeWidth={1} />
            <text x={x} y={H - 6} fontSize={11} fill={muted} textAnchor="middle">
              {y}
            </text>
          </g>
        );
      })}
    </svg>
  );

  if (!isStacked) return svg;

  // Stacked mode gets a small legend above the chart (units differ across
  // corpora — pages vs episodes — so we label it "documents").
  return (
    <div>
      <div style={{ display: "flex", gap: 16, marginBottom: 4, flexWrap: "wrap" }}>
        {(stacked ?? []).map((s) => (
          <span
            key={s.key}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: muted }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                background: s.color,
                borderRadius: 2,
                display: "inline-block",
              }}
            />
            {s.label}
          </span>
        ))}
        <span style={{ fontSize: 12, color: muted }}>documents / month</span>
      </div>
      {svg}
    </div>
  );
}
